// src/components/profile/profile.component.js
import React, { Component } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import styled, { createGlobalStyle } from "styled-components";
import ReactGA from "react-ga4";

const GlobalStyle = createGlobalStyle`
  html, body, #root {
    margin: 0;
    padding: 0;
    min-height: 100%;
    width: 100%;
  }

  body {
    overflow-x: hidden;
    overflow-y: auto;
    background: linear-gradient(135deg, #232d3d 0%, #2c374a 50%, #1b2230 100%);
    font-family: Arial, sans-serif;
  }

  * {
    box-sizing: border-box;
  }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background:
    radial-gradient(
      circle at top left,
      rgba(0, 125, 244, 0.2),
      transparent 34%
    ),
    linear-gradient(135deg, #232d3d 0%, #2c374a 50%, #1b2230 100%);
  display: flex;
  justify-content: center;
  padding: 22px 14px 42px;
`;

const ProfileCard = styled.div`
  width: 100%;
  max-width: 620px;
  overflow: visible;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.96);
  color: #111827;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.28);
`;

const Hero = styled.div`
  position: relative;
  height: 145px;
  overflow: hidden;
  border-radius: 24px 24px 0 0;
  background: ${(props) => props.bg || "#007DF4"};
`;

const HeroShade = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      circle at 20% 20%,
      rgba(255, 255, 255, 0.35),
      transparent 22%
    ),
    linear-gradient(180deg, rgba(0, 0, 0, 0.06) 0%, rgba(0, 0, 0, 0.12) 100%);
`;

const TopPill = styled.div`
  position: absolute;
  right: 18px;
  bottom: 18px;
  display: flex;
  gap: 8px;
`;

const StatPill = styled.div`
  min-width: 78px;
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(17, 24, 39, 0.82);
  backdrop-filter: blur(10px);
  color: #fff;
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 17px;
  font-weight: 800;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 11px;
  opacity: 0.86;
  margin-top: 3px;
`;

const Body = styled.div`
  padding: 0 24px 24px;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 0 0 24px 24px;
`;

const Avatar = styled.img`
  width: 92px;
  height: 92px;
  border-radius: 46px;
  object-fit: cover;
  border: 4px solid #fff;
  margin-top: -42px;
  background: #f3f4f6;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
`;

const ProfileTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
`;

const Name = styled.h1`
  margin: 10px 0 2px;
  font-size: 25px;
  line-height: 1.12;
`;

const Xp = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const ShareButton = styled.button`
  width: 38px;
  height: 38px;
  margin-top: 12px;
  border-radius: 19px;
  border: 1px solid #e5e7eb;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(10px);
  color: #6b7280;
  cursor: pointer;
  font-size: 18px;

  &:hover {
    background: #e5e7eb;
  }
`;

const Section = styled.section`
  margin-top: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  margin: 0 0 8px;
`;

const SoftBox = styled.div`
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 16px;
  padding: 14px;
`;

const BioText = styled.p`
  margin: 0;
  color: #374151;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
`;

const BadgeGrid = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const BadgeItem = styled.div`
  min-width: 76px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #374151;
  font-size: 11px;
`;

const BadgeIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 21px;
  background: linear-gradient(135deg, #ffcf33, #ff9f1c);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 21px;
  margin-bottom: 6px;
`;

const ActivityCard = styled.div`
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  background: #fff;
  margin-bottom: 12px;
`;

const ActivityImageWrap = styled.div`
  position: relative;
  height: 138px;
  background: #111827;
`;

const ActivityImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.08) 0%,
    rgba(0, 0, 0, 0.72) 100%
  );
`;

const ActivityTitle = styled.div`
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 14px;
  color: #fff;
  font-weight: 800;
  font-size: 17px;
`;

const ActivityMeta = styled.div`
  padding: 12px 14px;
  font-size: 13px;
  color: #6b7280;
`;

const CTABox = styled.div`
  margin-top: 22px;
  border-radius: 20px;
  padding: 16px;
  background: #111827;
  color: #fff;
`;

const CTAHeadline = styled.div`
  font-size: 18px;
  font-weight: 800;
`;

const CTAText = styled.div`
  margin-top: 5px;
  font-size: 13px;
  color: #cbd5e1;
  line-height: 1.45;
`;

const PrimaryButton = styled.button`
  width: 100%;
  margin-top: 14px;
  border: none;
  border-radius: 14px;
  padding: 14px 18px;
  background: #007df4;
  color: #fff;
  font-weight: 800;
  font-size: 15px;
  cursor: pointer;

  &:hover {
    background: #0068cc;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  margin-top: 10px;
  border-radius: 14px;
  padding: 12px 18px;
  border: 1px solid #2f80ed;
  background: transparent;
  color: #cde9ff;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
`;

const LoadingText = styled.div`
  color: #fff;
  padding: 60px 20px;
  text-align: center;
`;

class Profile extends Component {
  constructor(props) {
    super(props);

    const profileId = this.props.match.params.id;

    this.state = {
      profile: {
        id: profileId || "default",
        nickname: "Bealyo Member",
        bio: "Building consistency one challenge at a time.",
        imageUrl: "https://www.bealyo.com/images/prebealyoshare.png",
        rankscore: 0,
        followers: 0,
        activities: [],
        badges: [],
        mecolor: "#007DF4",
        joinedDate: "",
      },
      loading: true,
      error: null,
    };
  }

  componentDidMount() {
    const profileId = this.props.match.params.id;

    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });

    axios
      .post(process.env.REACT_APP_API_URL + "users/users/byid", {
        uid: profileId,
      })
      .then((response) => {
        const user = Array.isArray(response.data)
          ? response.data[0]
          : response.data?.user || response.data;

        if (!user) {
          throw new Error("No profile data returned");
        }

        const activities = user.goals_docs || user.goals || [];
        const followers = (user.userfollow_docs || []).filter(
          (item) =>
            item.askForFriendship === false ||
            item.askForFriendship === "false",
        ).length;

        this.setState({
          profile: {
            id: user._id || profileId,
            nickname: user.nickname || "Bealyo Member",
            bio: user.bio || "Building consistency one challenge at a time.",
            imageUrl:
              user.img_url && user.img_url !== ""
                ? process.env.REACT_APP_IMG_FOLDER + user.img_url
                : "https://www.bealyo.com/images/prebealyoshare.png",
            rankscore: user.rankscore || 0,
            followers,
            activities,
            badges: user.badges || [],
            mecolor: user.mecolor || "#007DF4",
            joinedDate: user.date
              ? new Date(user.date).toLocaleDateString()
              : "",
          },
          loading: false,
        });
      })
      .catch((error) => {
        console.error("Error fetching profile:", error);
        this.setState({
          error: "Failed to load profile",
          loading: false,
        });
      });
  }

  handleOpenApp = () => {
    const { profile } = this.state;

    ReactGA.event({
      category: "Profile",
      action: "View Profile In App Click",
      label: profile.nickname,
    });

    const deepLink = `bealyo-app://profile/${profile.id}`;
    const playStore =
      "https://play.google.com/store/apps/details?id=com.bealyo.bealyoapp";

    window.location.href = deepLink;

    setTimeout(() => {
      window.location.href = playStore;
    }, 1500);
  };

  handleShare = async () => {
    const { profile } = this.state;
    const shareUrl = `https://www.bealyo.com/profile/${profile.id}`;

    if (navigator.share) {
      await navigator.share({
        title: `${profile.nickname} on Bealyo`,
        text: `Check out ${profile.nickname}'s Bealyo journey.`,
        url: shareUrl,
      });
      return;
    }

    await navigator.clipboard.writeText(shareUrl);
    alert("Profile link copied");
  };

  renderBadges() {
    const { profile } = this.state;

    if (!profile.badges || profile.badges.length === 0) {
      return (
        <BioText>No badges yet. The journey is just getting started.</BioText>
      );
    }

    return (
      <BadgeGrid>
        {profile.badges.slice(0, 8).map((badge, index) => {
          const badgeKey = badge.k || badge.key || badge.name || "Badge";
          const badgeData = badge.v || badge;
          const earnedAt = badgeData.earnedAt
            ? new Date(badgeData.earnedAt).toLocaleDateString()
            : "";

          return (
            <BadgeItem key={`${badgeKey}-${index}`}>
              <BadgeIcon>★</BadgeIcon>
              <div>{String(badgeKey).replace(/_/g, " ")}</div>
              {earnedAt && <div style={{ opacity: 0.65 }}>{earnedAt}</div>}
            </BadgeItem>
          );
        })}
      </BadgeGrid>
    );
  }

  renderActivities() {
    const { profile } = this.state;

    if (!profile.activities || profile.activities.length === 0) {
      return <BioText>No public activities yet.</BioText>;
    }

    return profile.activities.slice(0, 5).map((activity) => {
      const imageUrl =
        activity.goal_imageurl && activity.goal_imageurl !== ""
          ? process.env.REACT_APP_IMG_FOLDER + activity.goal_imageurl
          : "https://www.bealyo.com/images/prebealyoshare.png";

      return (
        <ActivityCard key={activity._id || activity.goal_inviteid}>
          <ActivityImageWrap>
            <ActivityImage
              src={imageUrl}
              alt={activity.goal_title || "Activity"}
            />
            <ImageOverlay />
            <ActivityTitle>
              {activity.goal_title || "Bealyo Challenge"}
            </ActivityTitle>
          </ActivityImageWrap>
          <ActivityMeta>
            {activity.goal_desc || "A personal challenge on Bealyo."}
          </ActivityMeta>
        </ActivityCard>
      );
    });
  }

  render() {
    const { profile, loading, error } = this.state;

    if (loading) {
      return (
        <PageContainer>
          <GlobalStyle />
          <LoadingText>Loading profile...</LoadingText>
        </PageContainer>
      );
    }

    if (error) {
      return (
        <PageContainer>
          <GlobalStyle />
          <LoadingText>{error}</LoadingText>
        </PageContainer>
      );
    }

    const shareUrl = `https://www.bealyo.com/profile/${profile.id}`;
    const ogTitle = `${profile.nickname} on Bealyo`;
    const ogDescription =
      profile.bio ||
      "Follow this Bealyo journey and build consistency together.";

    return (
      <PageContainer>
        <Helmet>
          <title>{profile.nickname} | Bealyo</title>
          <meta name="description" content={ogDescription} />

          <meta property="al:android:package" content="com.bealyo.bealyoapp" />
          <meta
            property="al:android:url"
            content={`bealyo-app://profile/${profile.id}`}
          />
          <meta property="al:android:app_name" content="Bealyo" />

          <meta property="og:type" content="profile" />
          <meta property="og:url" content={shareUrl} />
          <meta property="og:title" content={ogTitle} />
          <meta property="og:description" content={ogDescription} />
          <meta property="og:image" content={profile.imageUrl} />

          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={shareUrl} />
          <meta property="twitter:title" content={ogTitle} />
          <meta property="twitter:description" content={ogDescription} />
          <meta property="twitter:image" content={profile.imageUrl} />
        </Helmet>

        <GlobalStyle />

        <ProfileCard>
          <Hero bg={profile.mecolor}>
            <HeroShade />
            <div
              style={{
                position: "absolute",
                top: 18,
                left: 22,
                zIndex: 5,
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                src="https://www.bealyo.com/images/logo/logo.png"
                alt="Bealyo"
                style={{
                  height: "28px",
                  objectFit: "contain",
                  opacity: 0.96,
                  filter: "drop-shadow(0 2px 10px rgba(0,0,0,0.18))",
                }}
              />
            </div>

            <TopPill>
              <StatPill>
                <StatNumber>{profile.followers}</StatNumber>
                <StatLabel>Followers</StatLabel>
              </StatPill>
              <StatPill>
                <StatNumber>{profile.activities.length}</StatNumber>
                <StatLabel>Activities</StatLabel>
              </StatPill>
            </TopPill>
          </Hero>

          <Body>
            <ProfileTop>
              <div style={{ zIndex: 1000 }}>
                <Avatar src={profile.imageUrl} alt={profile.nickname} />
                <Name>{profile.nickname}</Name>
                <Xp>
                  {profile.rankscore
                    ? `${profile.rankscore} XP`
                    : "Bealyo member"}
                  {profile.joinedDate ? ` · Joined ${profile.joinedDate}` : ""}
                </Xp>
              </div>

              <ShareButton
                onClick={this.handleShare}
                aria-label="Share profile"
              >
                ⤴
              </ShareButton>
            </ProfileTop>

            <Section>
              <SectionTitle>About me</SectionTitle>
              <SoftBox>
                <BioText>{profile.bio}</BioText>
              </SoftBox>
            </Section>

            <Section>
              <SectionTitle>Achievements</SectionTitle>
              <SoftBox>{this.renderBadges()}</SoftBox>
            </Section>

            <Section>
              <SectionTitle>
                {profile.activities.length} Activit
                {profile.activities.length === 1 ? "y" : "ies"}
              </SectionTitle>
              {this.renderActivities()}
            </Section>

            <CTABox>
              <CTAHeadline>Build your best version on Bealyo</CTAHeadline>
              <CTAText>
                Join challenges, track activities, share progress, and stay
                consistent with a supportive community.
              </CTAText>

              <PrimaryButton
                onClick={() =>
                  window.open(
                    "https://play.google.com/store/apps/details?id=com.bealyo.bealyoapp",
                    "_blank",
                  )
                }
              >
                Get Bealyo
              </PrimaryButton>

              <SecondaryButton onClick={this.handleOpenApp}>
                View Profile in App
              </SecondaryButton>
            </CTABox>
          </Body>
        </ProfileCard>
      </PageContainer>
    );
  }
}

export default Profile;
